// This file exports the copy keys reference documentation as a string
// The markdown content is imported from the adjacent .md file

export const copyKeysReferenceContent = `

This document provides a comprehensive reference for all copy keys used in the Charli application. These keys control various text content, configuration options, and UI elements throughout the application.

---

<h1 style="font-size: 2em; margin: 1em 0;">📁 Project & Initialization</h1>

### \`copy_project_initializing_message\`

**Type:** \`string\`
**Description:** Message displayed when a project is being initialized
**Example:** \`"Initializing project..."\`
**Used in:** \`screens/landing/tabs/project/projectLandingTileLayouts/TemporaryProjectSkeletonTile.tsx\`

<br>

### \`copy_project_placeholder_message\`

**Type:** \`string\`
**Description:** Placeholder message shown when no projects exist
**Example:** \`"Add a new project to get started. You can add a new project by clicking the button to the left..."\`
**Used in:** \`screens/landing/tabs/project/projectLandingTileLayouts/ProjectLandingSmallTile.tsx\`, \`screens/landing/tabs/project/projectLandingTileLayouts/ProjectLandingSmallTilePlaceholder.tsx\`

<br>

### \`copy_project_starting_message\`

**Type:** \`string\`
**Description:** Message displayed when a project analysis is starting
**Example:** \`"I am collecting the data and running a full analysis on this company..."\`
**Used in:** \`screens/landing/tabs/project/projectLandingTileLayouts/TemporaryProjectSkeletonTile.tsx\`, \`screens/landing/tabs/project/projectLandingTileLayouts/ProjectLandingSmallTile.tsx\`, \`screens/landing/tabs/project/projectLandingTileLayouts/ProjectLandingSmallTilePlaceholder.tsx\`

<h1 style="font-size: 2em; margin: 1em 0;">🔐 Authentication & Registration</h1>

### \`copy_login_heading\`

**Type:** \`string\`
**Description:** Main heading text on the login page
**Example:** \`"Market Research & Analysis on AutoPilot"\`
**Used in:** \`screens/login/components/LoginForm.tsx\`

<br>

### \`copy_login_list_items\`

**Type:** \`string[]\`
**Description:** Array of feature highlights displayed on the login page
**Example:**

\`\`\`json
[
  "Portfolio status at a glance with the Pi Quadrant Index...",
  "Private Company analysis is now available...",
  "Portfolio Sharing & Collaboration..."
]
\`\`\`

**Used in:** \`screens/login/components/LoginForm.tsx\`

<br>

### \`copy_registration_heading\`

**Type:** \`string\`
**Description:** Main heading on the registration page
**Example:** \`"Ready to Analyze your Investments?"\`
**Used in:** \`screens/login/Registration.tsx\`

<br>

### \`copy_registration_subheading\`

**Type:** \`string\`
**Description:** Subheading on the registration page
**Example:** \`"What to Expect"\`
**Used in:** \`screens/login/Registration.tsx\`

<br>

### \`copy_registration_list_items\`

**Type:** \`string[]\`
**Description:** Step-by-step process list for new users
**Example:**

\`\`\`json
["Register with Charli | Complete the registration form...", "Enter a Stock for Charli to Analyze...", "Explore Shared Portfolios..."]
\`\`\`

**Used in:** \`screens/login/Registration.tsx\`

<br>

### \`copy_registration_footer_text\`

**Type:** \`string[]\`
**Description:** Footer text paragraphs on registration page
**Example:**

\`\`\`json
["Charli is secure, trusted and used by thousands...", "**There is no credit card and no contract required..."]
\`\`\`

**Used in:** \`screens/login/Registration.tsx\`

<br>

### \`copy_registration_cta\`

**Type:** \`string\`
**Description:** Call-to-action text for registration
**Example:** \`"Register to Get Started"\`
**Used in:** \`screens/login/Registration.tsx\`

<br>

### \`copy_registration_submit_button\`

**Type:** \`string\`
**Description:** Text for the registration submit button
**Example:** \`"Register for Free"\`
**Used in:** \`screens/login/components/RegisterForm.tsx\`

<br>

### \`copy_registration_form_title\`

**Type:** \`string\`
**Description:** Title for the registration form
**Example:** \`"Start your Automated Analysis:"\`
**Used in:** \`screens/login/Registration.tsx\`

<h1 style="font-size: 2em; margin: 1em 0;">💳 Subscription & Pricing</h1>

### \`copy_subscription_features\`

**Type:** \`string[]\`
**Description:** List of subscription features with tier availability
**Example:**

\`\`\`json
[
  "Access to trusted FactSet Data | true | false | true | true | true",
  "Number of Concurrent Equity Research Reports Analysis | 2 | 6 | 10 | 20 | Custom"
]
\`\`\`

**Used in:** ⚠️ **UNUSED** - Only appears in admin documentation

<br>

### \`copy_subscription_plan\`

**Type:** \`PlanName\`
**Description:** Current subscription plan identifier
**Example:** \`"professional"\`
**Used in:** \`screens/panels/settings/tabs/subscription/SubscriptionUpgradeConfirm.tsx\`

<br>

### \`copy_subscription_plan_price\`

**Type:** \`string\`
**Description:** Price for the current subscription plan
**Example:** \`"99"\`
**Used in:** \`screens/panels/settings/tabs/subscription/SubscriptionUpgradeFeaturesHeader.tsx\`

<br>

### \`copy_subscription_tiers\`

**Type:** \`string[]\` (Array of stringified \`PricingTier\` objects)
**Description:** JSON-stringified array of pricing tier objects
**Example:**
\`\`\`
[
  {name: 'Starter', internalPlanName: 'free_trial', price: 'Free', description: 'For everyone. Get started...', features: [...]},
  {name: 'Professional', internalPlanName: 'professional', price: '$150', description: 'For Individual Investors...', features: [...]}
]
\`\`\`
**Used in:** \`screens/landing/components/pricing/PricingTable.tsx\`

<br>

### \`copy_ticker_pricing_tiers\`

**Type:** \`string[]\`
**Description:** Available pricing tier identifiers
**Example:** \`["tier_0", "tier_1", "tier_2", "tier_3", "tier_4", "tier_5"]\`
**Used in:** ⚠️ **UNUSED** - Only appears in admin documentation

<h1 style="font-size: 2em; margin: 1em 0;">🔧 Maintenance & System</h1>

### \`copy_maintenance_banner\`

**Type:** \`string\`
**Description:** Text for system maintenance banner (empty when no maintenance)
**Example:** \`""\` (empty string when no maintenance)
**Used in:** \`screens/landing/Landing.tsx\`

<br>

### \`copy_maintenance_banner_bg_color\`

**Type:** \`string\`
**Description:** Background color for maintenance banner
**Example:** \`"orange"\`
**Used in:** \`screens/landing/Landing.tsx\`

<br>

### \`copy_maintenance_banner_text_color\`

**Type:** \`string\`
**Description:** Text color for maintenance banner
**Example:** \`"gray.800"\`
**Used in:** \`screens/landing/Landing.tsx\`

<br>

### \`copy_version_tag\`

**Type:** \`string\`
**Description:** Version tag display text
**Example:** \`""\` (typically empty)
**Used in:** \`screens/panels/settings/tabs/about/About.tsx\`

<h1 style="font-size: 2em; margin: 1em 0;">🔍 Ticker & Company Search</h1>

### \`copy_ticker_placeholder_text\`

**Type:** \`string\`
**Description:** Placeholder text for ticker search input
**Example:** \`"Enter the stock or company that you would like me to analyze"\`
**Used in:** \`screens/collection/ProjectDetailView.tsx\`, \`screens/thread/ConversationDialog.tsx\`

<br>

### \`copy_ticker_exchange_options\`

**Type:** \`string[]\`
**Description:** Available stock exchange options
**Example:** \`["NYSE", "NASDAQ", "TSX", "BATS", "NYSEARCA", "PRIVATE", "PRIVATE.US", "LON", "SWX", "ETR", "EPA", "TSXV"]\`
**Used in:** \`screens/thread/useConversationDialogSuggestionLists.tsx\`, \`screens/dealfinder/components/DealFinderContent.tsx\`, \`screens/landing/tabs/admin/tickers/components/TickersFilters.tsx\`

<br>

### \`copy_ticker_type_options\`

**Type:** \`string[]\`
**Description:** Available security type options
**Example:** \`["equity", "etf", "mutual_funds"]\`
**Used in:** \`screens/thread/useConversationDialogSuggestionLists.tsx\`, \`screens/landing/tabs/admin/tickers/components/TickersFilters.tsx\`

<br>

### \`copy_ticker_private_restricted_list\`

**Type:** \`string[]\`
**Description:** List of restricted private company tickers
**Example:** \`["SHEERID", "SPACEX", "GLEAN", "DATABRICKS", "CLOUDERA"]\`
**Used in:** \`screens/thread/useConversationDialogSuggestionLists.tsx\`

<br>

### \`copy_ticker_excluded_exchanges\`

**Type:** \`string[]\`
**Description:** Exchanges to exclude from search
**Example:** \`["LON", "SWX", "ETR", "EPA", "TSXV"]\`
**Used in:** \`screens/thread/useConversationDialogSuggestionLists.tsx\`

<br>

### \`copy_ticker_excluded_types\`

**Type:** \`string[]\`
**Description:** Security types to exclude from search
**Example:** \`["etf", "mutual_funds"]\`
**Used in:** \`screens/thread/useConversationDialogSuggestionLists.tsx\`

<br>

### \`copy_ticker_type_order\`

**Type:** \`string[]\`
**Description:** Order preference for security types
**Example:** \`["equity"]\`
**Used in:** ⚠️ **UNUSED** - Only appears in admin documentation

<br>

### \`copy_ticker_filter_options\`

**Type:** \`string[]\`
**Description:** Available filter options for ticker search
**Example:** \`["equity", "etf", "mutual_funds", "private"]\`
**Used in:** \`screens/collection/views/AutocompleteSuggestion/AutoCompleteSuggestionList.tsx\`

<br>

### \`copy_ticker_upgrade_notification\`

**Type:** \`string\`
**Description:** Message shown when selected ticker requires plan upgrade
**Example:** \`"Please select a ticker from the available list. The ticker you selected is only available with an upgraded plan."\`
**Used in:** \`screens/collection/views/AutocompleteSuggestion/AutocompleteSuggestionWrapper.tsx\`, \`screens/thread/ConversationDialogInput.tsx\`

<br>

### \`copy_placeholder_tickers\`

**Type:** \`string[]\` (Array of stringified \`PlaceholderCompany\` objects)
**Description:** JSON-stringified array of placeholder company objects
**Example:**
\`\`\`
[
  {ticker: 'AAPL', securityType: 'equity', companyName: 'Apple Inc', logo: '', exchange: 'NASDAQ', overview: 'Apple Inc. is a multinational technology company...'},
  {ticker: 'MSFT', securityType: 'equity', companyName: 'Microsoft Corp', logo: '', exchange: 'NASDAQ', overview: 'Microsoft Corporation is a multinational technology company...'}
]
\`\`\`
**Used in:** \`screens/landing/tabs/dashboard/ProjectWatchList/ProjectWatchListGrid.tsx\`

<h1 style="font-size: 2em; margin: 1em 0;">💼 Deal Finder</h1>

### \`copy_default_deal_finder_symbols\`

**Type:** \`string[]\`
**Description:** Default symbols displayed in deal finder
**Example:** \`["JPM:NYSE", "BAC:NYSE", "AVGO:NASDAQ", "PLTR:NASDAQ", ...]\`
**Used in:** ⚠️ **UNUSED** - Only appears in admin documentation

<br>

### \`copy_deal_input_suggestions\`

**Type:** \`string[]\` (Array of stringified \`DealInputSuggestion\` objects)
**Description:** JSON-stringified array of deal input suggestion objects
**Notes:**: only use icons from https://react-icons.github.io/react-icons/icons/md/
**Example:**
\`\`\`
[
  {key: 'suggestion_1', question: 'AAPL', label: 'Apple Inc. (NASDAQ:AAPL)', type: 'suggestion'},
  {key: 'suggestion_2', question: 'Early stage financial services companies', label: 'Early stage financial services companies', type: 'suggestion'},
  {key: 'quick_link_1', question: 'Most active stocks in the last 30 days with revenues above $1B', label: 'Most Active', type: 'quick_link', icon: 'MdTrendingUp', iconColor: 'green.500'}
]
\`\`\`
**Used in:** \`screens/collection/views/AutocompleteSuggestion/DealActionButtons.tsx\`, \`screens/thread/useConversationDialogSuggestionLists.tsx\`

<br>

### \`copy_deal_filter_actions\`

**Type:** \`string[]\` (Array of stringified \`DealFilterAction\` objects)
**Description:** JSON-stringified array of deal filter action objects
**Notes:** frequency is in hours
**Example:**
\`\`\`
[
 {key: 'hot_deals_today', label: 'Most Active', url: 'https://charli.ai/most-active', frequency: 24, icon: 'MdWhatshot', iconColor: 'red.500'},
]
\`\`\`
**Used in:** \`screens/collection/views/AutocompleteSuggestion/FilterButtons.tsx\`

<br>

### \`copy_deal_due_diligence_description\`

**Type:** \`string\`
**Description:** Description for deal due diligence process
**Example:** \`"Charli can analyze this stock and provide an executive summary report..."\`
**Used in:** \`screens/dealfinder/components/modals/DueDiligenceModal.tsx\`

<br>

### \`copy_deal_due_diligence_description_upgrade\`

**Type:** \`string\`
**Description:** Message when upgrade is required for selected company
**Example:** \`"You have selected a company this is not included in your plan. Click 'Upgrade' to review our plans..."\`
**Used in:** \`screens/dealfinder/components/modals/DueDiligenceModal.tsx\`

<br>

### \`copy_deal_no_results_message\`

**Type:** \`string\`
**Description:** Message when no deals match search criteria
**Example:** \`"No deals match your search criteria, click the button below to reset the search and try again."\`
**Used in:** \`screens/dealfinder/components/DealFinderContent.tsx\`

<br>

### \`copy_deal_reset_search_button\`

**Type:** \`string\`
**Description:** Text for reset search button
**Example:** \`"Reset the search"\`
**Used in:** \`screens/dealfinder/components/DealFinderContent.tsx\`

<br>

### \`copy_deal_finder_loading_messages\`

**Type:** \`string[]\`
**Description:** Array of loading messages for deal finder
**Example:** \`["Searching for related deals...", "Finding top deals in sector...", ...]\`
**Used in:** \`screens/dealfinder/components/DealFinderContent.tsx\`

<br>

### \`copy_deal_finder_purchase_message\`

**Type:** \`string\`
**Description:** Message about purchase process for deal finder
**Example:** \`"Charli will email a copy of the PDF documents for investment scorecards..."\`
**Used in:** \`screens/dealfinder/components/modals/PurchasingCartForm.tsx\`

<br>

### \`copy_deal_menu_more_like_this\`

**Type:** \`string\`
**Description:** Text for "more like this" menu option
**Example:** \`"Find deals similar to"\`
**Used in:** \`screens/dealfinder/components/DealActionButtons.tsx\`

<br>

### \`copy_deal_menu_show_competitors\`

**Type:** \`string\`
**Description:** Text for "show competitors" menu option
**Example:** \`"Competitors to"\`
**Used in:** \`screens/dealfinder/components/DealActionButtons.tsx\`

<br>

### \`copy_deal_finder_action_buttons\`

**Type:** \`string\`
**Description:** Determines which buttons will be shown above the deal tiles on the deal finder.
**Options:** \`"all"\`, \`"filter"\`, \`"quick_links", \`"none"\`
**Example:** \`"quick_links"\`
**Used in:** \`screens/dealfinder/DealFinder.tsx\`

<br>

### \`copy_report_purchase_coupon_code\`

**Type:** \`string\`
**Description:** Coupon code for purchasing a report, managed by Delivery team
**Example:** \`"65OFF"\`
**Used in:** \`screens/dealfinder/components/modals/PurchasingCartForm.tsx\`

<br>


### \`copy_deal_finder_premium_tiers\`

**Type:** \`string[]\`
**Description:** Premium tier identifiers for deal finder
**Example:** \`["tier_4", "tier_5"]\`
**Used in:** \`screens/dealfinder/components/modals/PurchaseDealModal.tsx\`

<br>

### \`copy_deal_analyze_company_description\`

**Type:** \`string\`
**Description:** Description for company analysis feature
**Example:** \`"Advanced insights and detailed recommendations"\`
**Used in:** \`screens/dealfinder/components/modals/PurchaseDealRightSection.tsx\`

<br>

### \`copy_deal_finder_free_label\`

**Type:** \`string\`
**Description:** The price label on Analyze Company if user is not on free plan
**Example:** \`"Free"\`
**Used in:** \`screens/dealfinder/components/modals/PurchaseDealRightSection.tsx\`

<br>

### \`copy_placeholder_analysis_button_label\`

**Type:** \`string\`
**Description:** Label text for the analysis button in placeholder tiles
**Example:** \`"Start Analysis on"\`
**Used in:** \`screens/landing/tabs/project/projectLandingTileLayouts/ProjectLandingSmallTilePlaceholder.tsx\`

<h1 style="font-size: 2em; margin: 1em 0;">💰 Investment & Purchase</h1>

### \`copy_investment_purchase_heading\`

**Type:** \`string\`
**Description:** Heading for investment purchase section
**Example:** \`"PURCHASE INVESTMENT SCORECARD & REPORT"\`
**Used in:** \`screens/dealfinder/components/modals/PurchaseDealModal.tsx\`

<br>

### \`copy_markdown_investment_purchase_description\`

**Type:** \`markdown\`
**Description:** Markdown-formatted description of investment purchase options
**Example:** \`"Charli provides access to **investment scorecards**, **executive summary reports**..."\`
**Used in:** \`screens/dealfinder/components/modals/PurchaseDealModal.tsx\`

<br>

### \`copy_purchase_report_available_immediate\`

**Type:** \`string\`
**Description:** Message when report is available immediately
**Example:** \`"The report is available for download immediately by clicking below..."\`
**Used in:** \`screens/dealfinder/components/modals/PurchasingCartConfirm.tsx\`

<br>

### \`copy_purchase_report_available_later\`

**Type:** \`string\`
**Description:** Message when report will be generated later
**Example:** \`"The report will be generated and emailed to you when ready..."\`
**Used in:** \`screens/dealfinder/components/modals/PurchasingCartConfirm.tsx\`

<h1 style="font-size: 2em; margin: 1em 0;">🚀 Onboarding</h1>

### \`copy_onboarding_video_url\`
**Type:** \`string\`
**Description:** URL for the onboarding video
**Example:** \`"https://app.arcade.software/share/4Gg1BKDswzBUrPAZcJ4L"\`
**Used in:** \`/screens/landing/components/SidebarNavigation.tsx\`

### \`copy_markdown_onboarding_step_one\`

**Type:** \`markdown\`
**Description:** Complete markdown content for first onboarding step including heading and paragraphs
**Example:** \`"# Try searching for stocks\\n\\nThe copy you see here is a placeholder..."\`
**Used in:** \`screens/landing/components/popoverComponent/OnboardingVideoPopover.tsx\`

<br>

### \`copy_markdown_onboarding_step_two\`

**Type:** \`markdown\`
**Description:** Complete markdown content for second onboarding step including heading and list items
**Example:** \`"# Check out the Interactive Video\\n\\n- Charli will email you a summary report..."\`
**Used in:** \`screens/landing/components/popoverComponent/OnboardingVideoPopover.tsx\`

<br>

### \`copy_markdown_onboarding_step_two_heading\`

**Type:** \`markdown\`
**Description:** Heading for second onboarding step (legacy field, use copy_markdown_onboarding_step_two instead)
**Example:** \`"Check out the Interactive Video"\`
**Used in:** \`screens/landing/components/popoverComponent/OnboardingVideoPopover.tsx\`

<br>

### \`copy_markdown_onboarding_step_three\`

**Type:** \`markdown\`
**Description:** Complete markdown content for third onboarding step including heading and paragraphs
**Example:** \`"# Mouseover the Pi Quadrant Index for Instant Insights\\n\\nThe Pi Quadrant Index is a powerful tool..."\`
**Used in:** \`screens/landing/components/popoverComponent/OnboardingVideoPopover.tsx\`



### \`copy_onboarding_quadrant_url\`

**Type:** \`string\`
**Description:** URL for onboarding quadrant information
**Example:** \`"https://support.charli.ai/"\`
**Used in:** \`screens/landing/components/popoverComponent/OnboardingVideoPopover.tsx\`

<h1 style="font-size: 2em; margin: 1em 0;">📊 Charts & Help</h1>

### \`copy_chart_quadrant_help\`

**Type:** \`[string, string]\`
**Description:** Tuple containing help text and URL for quadrant chart
**Example:** \`["Learn more about the Portfolio Investment (Pi) Quadrant Chart", "https://charliai.com/wp-content/uploads/2025/01/Charli-AI-Pi-Quadrant-Index-.pdf"]\`
**Used in:** \`screens/landing/tabs/dashboard/HomeQuadrantChart.tsx\`, \`screens/landing/tabs/collections/ProjectMetrics.tsx\`



### \`copy_chart_buysellhold_help\`

**Type:** \`[string, string]\`
**Description:** Tuple containing help text and URL for buy/sell/hold chart
**Example:** \`["", ""]\` (currently empty)
**Used in:** \`screens/landing/tabs/collections/ProjectMetrics.tsx\`



### \`copy_chart_stockmarket_help\`

**Type:** \`[string, string]\`
**Description:** Tuple containing help text and URL for stock market chart
**Example:** \`["", ""]\` (currently empty)
**Used in:** \`screens/landing/tabs/collections/ProjectMetrics.tsx\`

<h1 style="font-size: 2em; margin: 1em 0;">💬 UI Placeholders & Messages</h1>

### \`copy_header_interaction_bar_placeholder\`

**Type:** \`string\`
**Description:** Placeholder text for header search bar (desktop)
**Example:** \`"Search private and public companies for investment insights"\`
**Used in:** \`screens/common/components/HeaderInteractionBar.tsx\`



### \`copy_header_interaction_bar_placeholder_mobile\`

**Type:** \`string\`
**Description:** Placeholder text for header search bar (mobile)
**Example:** \`"Search for deals"\`
**Used in:** \`screens/common/components/HeaderInteractionBar.tsx\`



### \`copy_promotion_code\`

**Type:** \`string\`
**Description:** Current promotion code (empty when no promotion)
**Example:** \`""\` (typically empty)
**Used in:** \`screens/panels/settings/tabs/subscription/SubscriptionUpgradeConfirm.tsx\`

<br>

### \`copy_typing_text_loop_delays\`

**Type:** \`string[]\`
**Description:** Delay values for typing animation loops
**Example:** \`["1500", "3000"]\`
**Used in:** \`screens/dealfinder/components/TypingText.tsx\`

<h1 style="font-size: 2em; margin: 1em 0;">⚙️ Configuration Arrays</h1>

### \`copy_information_extraction_format_options\`

**Type:** \`string[]\`
**Description:** Available format options for information extraction
**Example:**

\`\`\`json
["decimal:0", "decimal:2", "comma-separated", "short-scale", "percentage", "currency:USD", "date:%Y-%m-%d"]
\`\`\`

**Used in:** \`screens/landing/tabs/admin/informationExtractionConfig/components/UpsertTargetEntity.tsx\`

<h1 style="font-size: 2em; margin: 1em 0;">👍👎 Hotlink Actions</h1>

### \`copy_thumbs_up_hotlink_actions_answers\`

**Type:** \`string[]\` (Array of stringified \`HotlinkAction\` objects)
**Description:** JSON-stringified array of thumbs up hotlink action objects for answer feedback
**Example:**
\`\`\`json
[
  {
    "id": "like-answer",
    "label": "I like the answer",
    "feedbackMessage": "I like the answer",
    "feedbackAction": "thumbs_up",
    "className": "ch-thumbs-up-hotlink-1"
  },
  {
    "id": "good-qa-report",
    "label": "Good question and answer for the report",
    "feedbackMessage": "Good question and answer for the report",
    "feedbackAction": "thumbs_up"
  }
]
\`\`\`
**Used in:** \`screens/collection/views/AnswerCardPinned.tsx\`, \`screens/content/contentView/VerifiedAIPanel.tsx\`

<br>

### \`copy_thumbs_down_hotlink_actions_answers\`

**Type:** \`string[]\` (Array of stringified \`HotlinkAction\` objects)
**Description:** JSON-stringified array of thumbs down hotlink action objects for answer feedback
**Example:**
\`\`\`json
[
  {
    "id": "not-insightful-keep",
    "label": "Not that insightful, but keep it pinned",
    "feedbackMessage": "Not that insightful, but keep it pinned",
    "feedbackAction": "thumbs_down",
    "className": "ch-thumbs-down-hotlink-1"
  },
  {
    "id": "erroneous-unpin",
    "label": "Erroneous information, unpin the answer",
    "feedbackMessage": "Erroneous information, unpin the answer",
    "feedbackAction": "unpinned",
    "unpinReason": "erroneous",
    "className": "ch-thumbs-down-hotlink-2"
  }
]
\`\`\`
**Used in:** \`screens/collection/views/AnswerCardPinned.tsx\`, \`screens/content/contentView/VerifiedAIPanel.tsx\`

<br>

### \`copy_thumbs_up_hotlink_actions_tables\`

**Type:** \`string[]\` (Array of stringified \`HotlinkAction\` objects)
**Description:** JSON-stringified array of thumbs up hotlink action objects for table feedback
**Example:**
\`\`\`json
[
  {
    "id": "like-answer",
    "label": "I like the answer",
    "feedbackMessage": "I like the answer",
    "feedbackAction": "thumbs_up",
    "className": "ch-thumbs-up-hotlink-1"
  },
  {
    "id": "good-qa-report",
    "label": "Good question and answer for the report",
    "feedbackMessage": "Good question and answer for the report",
    "feedbackAction": "thumbs_up"
  }
]
\`\`\`
**Used in:** \`screens/content/contentView/components/SectionFooterActions.tsx\`

<br>

### \`copy_thumbs_down_hotlink_actions_tables\`

**Type:** \`string[]\` (Array of stringified \`HotlinkAction\` objects)
**Description:** JSON-stringified array of thumbs down hotlink action objects for table feedback
**Example:**
\`\`\`json
[
  {
    "id": "not-insightful-keep",
    "label": "Not that insightful, but keep it pinned",
    "feedbackMessage": "Not that insightful, but keep it pinned",
    "feedbackAction": "thumbs_down",
    "className": "ch-thumbs-down-hotlink-1"
  },
  {
    "id": "erroneous-unpin",
    "label": "Erroneous information, unpin the answer",
    "feedbackMessage": "Erroneous information, unpin the answer",
    "feedbackAction": "unpinned",
    "unpinReason": "erroneous",
    "className": "ch-thumbs-down-hotlink-2"
  }
]
\`\`\`
**Used in:** \`screens/content/contentView/components/SectionFooterActions.tsx\`

<h1 style="font-size: 2em; margin: 1em 0;">� Usage Notes</h1>

1. **Type Safety**: All copy keys are strongly typed through the \`CopiesTypeMapping\` interface
2. **Validation**: Use \`isDefinedCopyKey(key)\` to check if a string is a valid copy key
3. **Previews**: Some copy keys have associated preview components defined in \`copyPreviews\`
4. **Defaults**: All keys have default values defined in \`defaultCopiesValues\`

<h1 style="font-size: 2em; margin: 1em 0;">🔗 Related Types</h1>

- \`CopyTypedKey\`: Union type of all valid copy keys
- \`CopyValueMap\`: Maps each key to its corresponding value type
- \`CopyValueType<K>\`: Gets the value type for a specific key
- \`CopyPreviewComponent\`: React component type for copy previews`;
